import dayjs from 'dayjs';

export interface IntervalOption {
    readonly value: string;
    readonly label: string;
}

export enum TimeInterval {
    Hour = '1h',
    Day = '1d',
    Week = '1w',
    Month = '1M',
    Year = '1y',
}

const INTERVAL_LABELS: Readonly<Record<TimeInterval, string>> = {
    [TimeInterval.Hour]: '1 Hour',
    [TimeInterval.Day]: '1 Day',
    [TimeInterval.Week]: '1 Week',
    [TimeInterval.Month]: '1 Month',
    [TimeInterval.Year]: '1 Year',
};

const createIntervalOption = (interval: TimeInterval): IntervalOption => ({
    value: interval,
    label: INTERVAL_LABELS[interval],
});

const ALL_INTERVAL_OPTIONS = [
    TimeInterval.Hour,
    TimeInterval.Day,
    TimeInterval.Week,
    TimeInterval.Month,
    TimeInterval.Year,
].map(createIntervalOption);

type RangeConditionContext = {
    days: number;
    years: number;
};

const RANGES_CONFIG: ReadonlyArray<{
    condition: (context: RangeConditionContext) => boolean;
    intervals: ReadonlyArray<TimeInterval>;
}> = [
    {
        condition: ({ days }) => days <= 7,
        intervals: [TimeInterval.Hour, TimeInterval.Day, TimeInterval.Week],
    },
    {
        condition: ({ days }) => days <= 90,
        intervals: [TimeInterval.Day, TimeInterval.Week, TimeInterval.Month],
    },
    {
        condition: ({ years }) => years < 1,
        intervals: [TimeInterval.Week, TimeInterval.Month],
    },
    {
        condition: () => true,
        intervals: [TimeInterval.Week, TimeInterval.Month, TimeInterval.Year],
    },
];

export const getAvailableIntervals = (from: dayjs.Dayjs | null, to: dayjs.Dayjs | null): IntervalOption[] => {
    if (!from || !to) {
        return ALL_INTERVAL_OPTIONS;
    }

    const context: RangeConditionContext = {
        days: to.diff(from, 'day'),
        years: to.diff(from, 'year'),
    };

    const { intervals } = RANGES_CONFIG.find(config => config.condition(context))!;

    return [...intervals.map(createIntervalOption)];
};
